import React, { useState, useEffect } from "react";
import {
  signInWithEmail,
  signUpWithEmail,
  signInWithGoogle,
  logOut,
  resetPassword,
  onAuthChange,
  getUserData,
  isDisposableEmail,
  resendVerificationEmail,
} from "./firebase-auth";
import { redirectToPaymentLink } from "./stripe-checkout";
import { stripeConfig } from "./stripe-config";

// Login Modal Component
export const LoginModal = ({ isOpen, onClose, onSwitchToSignup }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError("");
      setLoading(false);
      setShowForgotPassword(false);
      setResetEmailSent(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { user, error: loginError } = await signInWithEmail(email, password);

    if (loginError) {
      setError(loginError);
      setLoading(false);
    } else {
      onClose();
    }
  };

  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);

    const { user, error: loginError } = await signInWithGoogle();

    if (loginError) {
      setError(loginError);
      setLoading(false);
    } else {
      onClose();
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const { error: resetError } = await resetPassword(email);

    if (resetError) {
      setError(resetError);
    } else {
      setResetEmailSent(true);
    }
    setLoading(false);
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>

        {!showForgotPassword ? (
          <>
            <h2>Welcome Back</h2>
            <p className="auth-subtitle">Sign in to access your portfolios</p>

            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button
                type="button"
                className="forgot-password-btn"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot password?
              </button>

              <button
                type="submit"
                className="auth-btn primary"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="auth-divider">
              <span>OR</span>
            </div>

            <button
              className="auth-btn google"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path
                  fill="#4285F4"
                  d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
                />
                <path
                  fill="#34A853"
                  d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"
                />
                <path
                  fill="#FBBC05"
                  d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"
                />
                <path
                  fill="#EA4335"
                  d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="auth-footer">
              Don't have an account?{" "}
              <button className="auth-link" onClick={onSwitchToSignup}>
                Sign up
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Reset Password</h2>
            <p className="auth-subtitle">
              Enter your email to receive a reset link
            </p>

            {error && <div className="auth-error">{error}</div>}
            {resetEmailSent && (
              <div className="auth-success">
                Password reset email sent! Check your inbox.
              </div>
            )}

            <form onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                className="auth-btn primary"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>

            <button
              className="auth-link"
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
              }}
            >
              ← Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Signup Modal Component
export const SignUpModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // Honeypot field for bot detection
  const [verificationSent, setVerificationSent] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError("");
      setLoading(false);
      setVerificationSent(false);
      setHoneypot("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Honeypot check - if filled, it's a bot
    if (honeypot !== "") {
      console.log("Bot detected - honeypot filled");
      setLoading(false);
      return; // Silently reject
    }

    // Check for disposable email
    if (isDisposableEmail(email)) {
      setError(
        "Please use a permanent email address. Temporary email services are not allowed."
      );
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    const {
      user,
      error: signupError,
      verificationSent: emailSent,
    } = await signUpWithEmail(email, password, displayName);

    if (signupError) {
      setError(signupError);
      setLoading(false);
    } else {
      setVerificationSent(true);
      // Don't close modal - show verification message instead
    }
  };

  const handleGoogleSignup = async () => {
    setError("");
    setLoading(true);

    const { user, error: signupError } = await signInWithGoogle();

    if (signupError) {
      setError(signupError);
      setLoading(false);
    } else {
      onClose();
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>

        <h2>Create Account</h2>
        <p className="auth-subtitle">Start rebalancing your portfolio today</p>

        {error && <div className="auth-error">{error}</div>}

        {verificationSent ? (
          <div className="verification-sent">
            <div className="verification-icon">📧</div>
            <h3>Check Your Email!</h3>
            <p>
              We've sent a verification link to <strong>{email}</strong>
            </p>
            <p className="verification-instructions">
              Please click the link in the email to verify your account, then
              return here to sign in.
            </p>
            <button
              className="auth-btn secondary"
              onClick={async () => {
                const { error } = await resendVerificationEmail();
                if (!error) {
                  alert("Verification email resent!");
                }
              }}
            >
              Resend Verification Email
            </button>
            <button className="auth-btn primary" onClick={onSwitchToLogin}>
              Go to Sign In
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleEmailSignup}>
              {/* Honeypot field - hidden from users, catches bots */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: "1px",
                  height: "1px",
                  opacity: 0,
                  pointerEvents: "none",
                }}
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
              />

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <small>Minimum 6 characters</small>
              </div>

              <button
                type="submit"
                className="auth-btn primary"
                disabled={loading}
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <div className="auth-divider">
              <span>OR</span>
            </div>

            <button
              className="auth-btn google"
              onClick={handleGoogleSignup}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18">
                <path
                  fill="#4285F4"
                  d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"
                />
                <path
                  fill="#34A853"
                  d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"
                />
                <path
                  fill="#FBBC05"
                  d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"
                />
                <path
                  fill="#EA4335"
                  d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="auth-footer">
              Already have an account?{" "}
              <button className="auth-link" onClick={onSwitchToLogin}>
                Sign in
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// User Profile Dropdown Component
export const UserProfile = ({ user, userData, onUpgrade, onLogout, portfolios, customAssetMappings, rebalanceThreshold, customAllocation, targetAllocation }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [exportedData, setExportedData] = useState(false);

  const handleLogout = async () => {
    await logOut();
    setShowDropdown(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleUpgrade = () => {
    setShowDropdown(false);
    if (onUpgrade) {
      onUpgrade();
    }
  };

  const exportUserData = () => {
    const exportData = {
      exportDate: new Date().toISOString(),
      userEmail: user.email,
      accountCreated: userData?.createdAt,
      
      portfolios: (portfolios || []).map(p => ({
        id: p.id,
        name: p.name,
        totalValue: p.holdings.reduce((sum, h) => sum + h.marketValue, 0),
        holdings: p.holdings.map(h => ({
          symbol: h.symbol,
          quantity: h.quantity,
          price: h.price,
          marketValue: h.marketValue,
          assetClass: h.assetClass
        }))
      })),
      
      customAssetMappings: customAssetMappings || {},
      
      settings: {
        rebalanceThreshold: rebalanceThreshold,
        customAllocation: customAllocation,
        targetAllocation: targetAllocation
      }
    };
    
    const blob = new Blob(
      [JSON.stringify(exportData, null, 2)], 
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portfolio-rebalancer-data-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    setExportedData(true);
  };

  const handleDeleteAccount = () => {
    // Check if user has active subscription
    if (userData?.tier === 'premium') {
      alert('⚠️ Please cancel your Premium subscription before deleting your account.\n\nClick "Upgrade to Premium" to manage your subscription and downgrade to Free.');
      setShowDropdown(false);
      if (onUpgrade) {
        onUpgrade();
      }
      return;
    }
    
    setShowDropdown(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      alert('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);

    try {
      // Import deleteUser from Firebase
      const { deleteUser } = await import('firebase/auth');
      const { deleteDoc, doc } = await import('firebase/firestore');
      const { db } = await import('./firebase-auth');
      
      // Delete user data from Firestore
      await deleteDoc(doc(db, 'users', user.uid));
      
      // Delete Firebase Auth account
      await deleteUser(user);
      
      // User is automatically logged out
      alert('Your account has been deleted.');
    } catch (error) {
      console.error('Error deleting account:', error);
      
      if (error.code === 'auth/requires-recent-login') {
        alert('For security, please log out and log back in before deleting your account.');
      } else {
        alert('Error deleting account. Please try again or contact support.');
      }
      
      setIsDeleting(false);
    }
  };

  const userInitials = userData?.displayName
    ? userData.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : user.email[0].toUpperCase();

  return (
    <>
      <div className="user-profile">
        <button
          className="user-avatar"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          {userInitials}
        </button>

        {showDropdown && (
          <div className="user-dropdown">
            <div className="user-info">
              <div className="user-name">{userData?.displayName || "User"}</div>
              <div className="user-email">{user.email}</div>
              <div className={`user-tier ${userData?.tier}`}>
                {userData?.tier === "premium" ? "⭐ Premium" : "🆓 Free"}
              </div>
            </div>

            <div className="dropdown-divider" />

            {userData?.tier !== "premium" && (
              <button className="dropdown-item upgrade" onClick={handleUpgrade}>
                <span>✨ Upgrade to Premium</span>
              </button>
            )}

            <button className="dropdown-item" onClick={handleLogout}>
              <span>🚪 Log Out</span>
            </button>

            <div className="dropdown-divider" />

            <button className="dropdown-item delete-account" onClick={handleDeleteAccount}>
              <span>🗑️ Delete Account</span>
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="auth-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-account-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setShowDeleteConfirm(false)}
            >
              ×
            </button>

            <h2>⚠️ Delete Account</h2>
            
            <div className="delete-warning-box">
              <p><strong>This action cannot be undone.</strong></p>
              <p>All your data will be permanently deleted:</p>
              <ul>
                <li>✗ All portfolios and holdings</li>
                <li>✗ Custom asset classifications</li>
                <li>✗ Account settings</li>
                <li>✗ Rebalancing preferences</li>
              </ul>
            </div>

            {!exportedData && (
              <div className="export-data-section">
                <h4>📥 Export Your Data First?</h4>
                <p className="export-hint">
                  Save a copy of your portfolios before deleting
                </p>
                <button 
                  className="export-data-btn"
                  onClick={exportUserData}
                >
                  <span className="icon">📄</span>
                  <span className="label">Download Complete Data (JSON)</span>
                </button>
              </div>
            )}

            {exportedData && (
              <div className="export-success">
                ✓ Data exported successfully
              </div>
            )}

            <div className="confirm-delete-section">
              <label>
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="delete-confirm-input"
              />
            </div>

            <div className="delete-modal-actions">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="cancel-delete-btn"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="confirm-delete-btn"
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Upgrade Prompt Component (shown when free user tries to add 2nd portfolio)
export const UpgradePrompt = ({ isOpen, onClose, onUpgrade, user }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("monthly");

  const handleUpgrade = async () => {
    if (!user) {
      alert("Please sign in to upgrade");
      return;
    }

    setLoading(true);

    try {
      // Redirect to Stripe Payment Link
      redirectToPaymentLink(selectedPlan, user.email, user.uid);
    } catch (error) {
      console.error("Upgrade error:", error);
      alert("Error starting checkout. Please try again or contact support.");
      setLoading(false);
    }
  };

  // Add ESC key to close - MUST be before early return
  React.useEffect(() => {
    if (!isOpen) return; // Don't add listener if modal is closed

    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose, loading]);

  // Don't render if not open - MUST be after all hooks
  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          ×
        </button>

        <div className="upgrade-icon">🚀</div>
        <h2>Upgrade to Premium</h2>
        <p className="upgrade-subtitle">
          Track unlimited portfolios and get advanced features
        </p>

        <div className="upgrade-comparison">
          <div className="upgrade-column free">
            <h3>Free</h3>
            <div className="upgrade-price">$0</div>
            <ul>
              <li>✓ 1 portfolio</li>
              <li>✓ Portfolio rebalancing</li>
              <li>✓ Monte Carlo simulation</li>
              <li>✓ Diversification score</li>
              <li>✗ Aggregated view</li>
              <li>✗ Multiple portfolios</li>
            </ul>
          </div>

          <div className="upgrade-column premium">
            <div className="upgrade-badge">Best Value</div>
            <h3>Premium</h3>
            <div className="upgrade-price">
              $10<span>/month</span>
            </div>
            <ul>
              <li>✓ Unlimited portfolios</li>
              <li>✓ Aggregated view</li>
              <li>✓ All free features</li>
              <li>✓ Priority support</li>
              <li>✓ Future: 13F tracking</li>
              <li>✓ Future: Tax-loss harvesting</li>
            </ul>
          </div>
        </div>

        <div className="plan-selector">
          <h3>Select Your Plan</h3>
          <div className="plan-options">
            <label
              className={`plan-option ${
                selectedPlan === "monthly" ? "selected" : ""
              }`}
            >
              <input
                type="radio"
                name="plan"
                value="monthly"
                checked={selectedPlan === "monthly"}
                onChange={(e) => setSelectedPlan(e.target.value)}
              />
              <div className="plan-info">
                <strong>Monthly</strong>
                <span className="plan-price">$10/month</span>
              </div>
            </label>

            <label
              className={`plan-option ${
                selectedPlan === "annual" ? "selected" : ""
              }`}
            >
              <input
                type="radio"
                name="plan"
                value="annual"
                checked={selectedPlan === "annual"}
                onChange={(e) => setSelectedPlan(e.target.value)}
              />
              <div className="plan-info">
                <strong>Annual</strong>
                <span className="plan-price">$60/year</span>
                <span className="plan-savings">Save 50%</span>
              </div>
            </label>
          </div>
        </div>

        <button
          className="upgrade-btn-large"
          onClick={handleUpgrade}
          disabled={loading}
        >
          {loading ? "Redirecting to Checkout..." : "Continue to Checkout →"}
        </button>

        <p className="upgrade-note">
          Secure payment powered by Stripe • Cancel anytime
        </p>
      </div>
    </div>
  );
};

// Auth CSS (add to your existing styles)
export const authStyles = `
  /* Auth Modal Overlay */
  .auth-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(4px);
  }

  .auth-modal {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-radius: 16px;
    padding: 40px;
    max-width: 440px;
    width: 90%;
    position: relative;
    border: 1px solid rgba(71, 85, 105, 0.5);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  .modal-close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    font-size: 2rem;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.2s;
    z-index: 10001;
    padding: 8px;
  }

  .modal-close:hover {
    color: #f1f5f9;
  }

  .auth-modal h2 {
    color: #f1f5f9;
    margin-bottom: 8px;
    font-size: 1.75rem;
  }

  .auth-subtitle {
    color: #94a3b8;
    margin-bottom: 24px;
  }

  .auth-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid #ef4444;
    color: #fca5a5;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 0.875rem;
  }

  .auth-success {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid #10b981;
    color: #6ee7b7;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 0.875rem;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    display: block;
    color: #cbd5e1;
    margin-bottom: 8px;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .form-group input {
    width: 100%;
    padding: 12px 16px;
    background: rgba(15, 23, 42, 0.8);
    border: 1px solid #334155;
    border-radius: 8px;
    color: #f1f5f9;
    font-size: 1rem;
    transition: border-color 0.2s;
  }

  .form-group input:focus {
    outline: none;
    border-color: #3b82f6;
  }

  .form-group small {
    display: block;
    color: #64748b;
    font-size: 0.75rem;
    margin-top: 4px;
  }

  .forgot-password-btn {
    background: none;
    border: none;
    color: #3b82f6;
    font-size: 0.875rem;
    cursor: pointer;
    margin-bottom: 16px;
    padding: 0;
  }

  .forgot-password-btn:hover {
    text-decoration: underline;
  }

  .auth-btn {
    width: 100%;
    padding: 14px 24px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s;
    border: none;
  }

  .auth-btn.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  .auth-btn.primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }

  .auth-btn.secondary {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
    border: 1px solid #3b82f6;
  }

  .auth-btn.secondary:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.2);
  }

  .auth-btn.google {
    background: white;
    color: #1f2937;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 1px solid #e5e7eb;
  }

  .auth-btn.google:hover:not(:disabled) {
    background: #f9fafb;
  }

  .auth-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .auth-divider {
    margin: 24px 0;
    text-align: center;
    position: relative;
  }

  .auth-divider::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #334155;
  }

  .auth-divider span {
    background: #1e293b;
    padding: 0 16px;
    position: relative;
    color: #64748b;
    font-size: 0.875rem;
  }

  /* Email Verification Sent */
  .verification-sent {
    text-align: center;
    padding: 20px 0;
  }

  .verification-icon {
    font-size: 4rem;
    margin-bottom: 16px;
    animation: bounce 2s ease-in-out infinite;
  }

  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  .verification-sent h3 {
    color: #f1f5f9;
    margin-bottom: 12px;
    font-size: 1.5rem;
  }

  .verification-sent p {
    color: #94a3b8;
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .verification-instructions {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid #3b82f6;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
    color: #93c5fd !important;
  }

  .verification-sent .auth-btn.secondary {
    margin-bottom: 12px;
  }

  .auth-footer {
    margin-top: 24px;
    text-align: center;
    color: #94a3b8;
    font-size: 0.875rem;
  }

  .auth-link {
    background: none;
    border: none;
    color: #3b82f6;
    cursor: pointer;
    font-size: inherit;
    font-weight: 600;
  }

  .auth-link:hover {
    text-decoration: underline;
  }

  /* User Profile */
  .user-profile {
    position: relative;
  }

  .user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    font-weight: 700;
    border: 2px solid rgba(59, 130, 246, 0.3);
    cursor: pointer;
    transition: all 0.3s;
  }

  .user-avatar:hover {
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
  }

  .user-dropdown {
    position: absolute;
    top: 50px;
    right: 0;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 12px;
    padding: 12px 0;
    min-width: 240px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
    z-index: 1000;
  }

  .user-info {
    padding: 12px 16px;
  }

  .user-name {
    color: #f1f5f9;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .user-email {
    color: #94a3b8;
    font-size: 0.875rem;
    margin-bottom: 8px;
  }

  .user-tier {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .user-tier.free {
    background: rgba(100, 116, 139, 0.2);
    color: #94a3b8;
  }

  .user-tier.premium {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
    color: #a78bfa;
  }

  .dropdown-divider {
    height: 1px;
    background: #334155;
    margin: 8px 0;
  }

  .dropdown-item {
    width: 100%;
    padding: 12px 16px;
    background: none;
    border: none;
    color: #cbd5e1;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s;
    font-size: 0.9rem;
  }

  .dropdown-item:hover {
    background: rgba(51, 65, 85, 0.5);
  }

  .dropdown-item.upgrade {
    color: #a78bfa;
    font-weight: 600;
  }

  .dropdown-item.delete-account {
    color: #ef4444;
  }

  .dropdown-item.delete-account:hover {
    background: rgba(239, 68, 68, 0.1);
  }

  /* Delete Account Modal */
  .delete-account-modal {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-radius: 16px;
    padding: 32px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    border: 1px solid rgba(239, 68, 68, 0.3);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  }

  .delete-account-modal h2 {
    color: #ef4444;
    margin-bottom: 20px;
    font-size: 1.5rem;
  }

  .delete-warning-box {
    background: rgba(239, 68, 68, 0.1);
    border: 2px solid #ef4444;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }

  .delete-warning-box p {
    color: #f1f5f9;
    margin-bottom: 12px;
  }

  .delete-warning-box strong {
    color: #ef4444;
  }

  .delete-warning-box ul {
    list-style: none;
    margin: 12px 0 0 0;
    padding: 0;
  }

  .delete-warning-box li {
    color: #cbd5e1;
    padding: 6px 0;
    font-size: 0.95rem;
  }

  .export-data-section {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }

  .export-data-section h4 {
    color: #f1f5f9;
    margin-bottom: 8px;
    font-size: 1rem;
  }

  .export-hint {
    color: #94a3b8;
    font-size: 0.875rem;
    margin-bottom: 12px;
  }

  .export-data-btn {
    width: 100%;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    border: none;
    padding: 12px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.3s ease;
  }

  .export-data-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }

  .export-success {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid #10b981;
    border-radius: 8px;
    padding: 12px;
    color: #10b981;
    text-align: center;
    margin-bottom: 20px;
    font-weight: 600;
  }

  .confirm-delete-section {
    margin-bottom: 20px;
  }

  .confirm-delete-section label {
    display: block;
    color: #cbd5e1;
    margin-bottom: 8px;
    font-size: 0.95rem;
  }

  .confirm-delete-section strong {
    color: #ef4444;
    font-weight: 700;
  }

  .delete-confirm-input {
    width: 100%;
    padding: 12px;
    background: rgba(15, 23, 42, 0.8);
    border: 2px solid #334155;
    border-radius: 8px;
    color: #f1f5f9;
    font-size: 1rem;
    font-weight: 600;
  }

  .delete-confirm-input:focus {
    outline: none;
    border-color: #ef4444;
  }

  .delete-modal-actions {
    display: flex;
    gap: 12px;
  }

  .cancel-delete-btn {
    flex: 1;
    padding: 12px 24px;
    background: rgba(51, 65, 85, 0.5);
    border: 1px solid #334155;
    border-radius: 8px;
    color: #cbd5e1;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .cancel-delete-btn:hover:not(:disabled) {
    background: rgba(51, 65, 85, 0.8);
  }

  .cancel-delete-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .confirm-delete-btn {
    flex: 1;
    padding: 12px 24px;
    background: #ef4444;
    border: none;
    border-radius: 8px;
    color: white;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .confirm-delete-btn:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-2px);
  }

  .confirm-delete-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  /* Upgrade Modal */
  .upgrade-modal {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-radius: 16px;
    padding: 40px;
    max-width: 700px;
    width: 90%;
    position: relative;
    border: 1px solid rgba(71, 85, 105, 0.5);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    text-align: center;
  }

  .upgrade-icon {
    font-size: 4rem;
    margin-bottom: 16px;
  }

  .upgrade-subtitle {
    color: #94a3b8;
    margin-bottom: 32px;
  }

  .upgrade-comparison {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
  }

  .upgrade-column {
    background: rgba(15, 23, 42, 0.6);
    border: 2px solid #334155;
    border-radius: 12px;
    padding: 24px;
    position: relative;
  }

  .upgrade-column.premium {
    border-color: #3b82f6;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  }

  .upgrade-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    padding: 4px 16px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .upgrade-column h3 {
    color: #f1f5f9;
    margin-bottom: 12px;
  }

  .upgrade-price {
    font-size: 3rem;
    font-weight: 800;
    color: #f1f5f9;
    margin-bottom: 24px;
  }

  .upgrade-price span {
    font-size: 1.25rem;
    color: #94a3b8;
  }

  .upgrade-column ul {
    list-style: none;
    padding: 0;
    margin: 0 0 24px 0;
    text-align: left;
  }

  .upgrade-column li {
    padding: 8px 0;
    color: #cbd5e1;
    font-size: 0.875rem;
  }

  .upgrade-btn {
    width: 100%;
    padding: 14px 24px;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  .upgrade-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
  }

  /* Plan Selector */
  .plan-selector {
    margin: 32px 0;
    padding: 24px;
    background: rgba(15, 23, 42, 0.5);
    border-radius: 12px;
    border: 1px solid #334155;
  }

  .plan-selector h3 {
    color: #f1f5f9;
    margin-bottom: 16px;
    text-align: center;
  }

  .plan-options {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .plan-option {
    flex: 1;
    min-width: 200px;
    padding: 20px;
    background: rgba(30, 41, 59, 0.5);
    border: 2px solid #334155;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .plan-option:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
  }

  .plan-option.selected {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.2);
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }

  .plan-option input[type="radio"] {
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #3b82f6;
  }

  .plan-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .plan-info strong {
    color: #f1f5f9;
    font-size: 1.1rem;
  }

  .plan-price {
    color: #94a3b8;
    font-size: 0.95rem;
  }

  .plan-savings {
    color: #10b981;
    font-size: 0.85rem;
    font-weight: 600;
  }

  .upgrade-btn-large {
    width: 100%;
    padding: 18px 32px;
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-weight: 700;
    font-size: 1.1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  }

  .upgrade-btn-large:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(59, 130, 246, 0.5);
  }

  .upgrade-btn-large:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .upgrade-note {
    color: #64748b;
    font-size: 0.875rem;
    text-align: center;
    margin-top: 16px;
  }

  @media (max-width: 768px) {
    .upgrade-comparison {
      grid-template-columns: 1fr;
    }
    
    .plan-options {
      flex-direction: column;
    }
  }
`;
